import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, TOKEN_KEY } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge } from "@/components/Brutal";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X as XIcon, Printer, CheckCircle2, Ban, Pencil, Trash2, Search, DollarSign, MessageCircle } from "lucide-react";
import { openWhatsapp, formatPaymentOrderMessage } from "@/lib/whatsapp";

const FORMAS = [
  { value: "PIX", label: "PIX" },
  { value: "Transferencia", label: "Transferência" },
  { value: "Dinheiro", label: "Dinheiro" },
  { value: "Cheque", label: "Cheque" },
  { value: "Boleto", label: "Boleto" },
  { value: "Cartao", label: "Cartão" },
];

const UNIDADES = ["Serviço", "UN", "PC", "PCT", "Caixa", "Fardo", "Saco", "Litro", "Kilo", "Metro", "Rolo", "Par", "Cuba", "Hora", "Diária", "Mês"];

const emptyItem = () => ({ descricao: "", quantidade: 1, unidade: "Serviço", valor_unitario: 0 });
const emptyForm = () => ({
  beneficiario: "", cnpj_cpf: "", descricao: "",
  data_vencimento: "", forma_pagamento: "PIX", dados_pagamento: "",
  observacoes: "", itens: [emptyItem()],
});

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentOrders() {
  const { user } = useAuth();
  const canManage = user && (user.role === "admin" || user.role === "compras");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pendente: 0, total_pago: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [payingObs, setPayingObs] = useState("");
  const [cancelingId, setCancelingId] = useState(null);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/payment-orders${qs}`);
      setItems(data.rows || []);
      setMeta({ total: data.total || 0, total_pendente: data.total_pendente || 0, total_pago: data.total_pago || 0 });
    } catch { toast.error("Erro ao carregar ordens"); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => setForm((f) => ({ ...f, itens: [...f.itens, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, itens: f.itens.filter((_, idx) => idx !== i) }));
  const setItemField = (i, k, v) => setForm((f) => {
    const copy = [...f.itens];
    copy[i] = { ...copy[i], [k]: v };
    return { ...f, itens: copy };
  });
  const total = form.itens.reduce((s, it) => {
    const q = parseFloat(it.quantidade) || 0;
    const vu = parseFloat(it.valor_unitario) || 0;
    return s + q * vu;
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.beneficiario.trim()) { toast.error("Beneficiário é obrigatório"); return; }
    const cleanItens = form.itens
      .filter((i) => (i.descricao || "").trim() && parseFloat(i.quantidade) > 0 && parseFloat(i.valor_unitario) > 0)
      .map((i) => {
        const q = parseFloat(i.quantidade);
        const vu = parseFloat(i.valor_unitario);
        return {
          descricao: i.descricao.trim(),
          quantidade: q,
          unidade: i.unidade || "UN",
          valor_unitario: vu,
          valor: Math.round(q * vu * 100) / 100,
        };
      });
    if (cleanItens.length === 0) { toast.error("Adicione ao menos um item com descrição, quantidade e valor unitário"); return; }
    try {
      const payload = { ...form, itens: cleanItens };
      if (editingId) {
        await api.put(`/payment-orders/${editingId}`, payload);
        toast.success("Ordem atualizada");
      } else {
        await api.post("/payment-orders", payload);
        toast.success("Ordem criada");
      }
      setOpen(false); setForm(emptyForm()); setEditingId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const startEdit = (op) => {
    setEditingId(op.id);
    setForm({
      beneficiario: op.beneficiario || "",
      cnpj_cpf: op.cnpj_cpf || "",
      descricao: op.descricao || "",
      data_vencimento: (op.data_vencimento || "").slice(0, 10),
      forma_pagamento: op.forma_pagamento || "PIX",
      dados_pagamento: op.dados_pagamento || "",
      observacoes: op.observacoes || "",
      itens: (op.itens || []).map((it) => ({
        descricao: it.descricao,
        quantidade: it.quantidade || 1,
        unidade: it.unidade || "UN",
        valor_unitario: it.valor_unitario || (it.quantidade ? Math.round((it.valor || 0) / it.quantidade * 100) / 100 : (it.valor || 0)),
      })),
    });
    setOpen(true);
  };

  const markPaid = async () => {
    try {
      await api.post(`/payment-orders/${payingId}/mark-paid`, { observacoes: payingObs });
      toast.success("Marcada como PAGA");
      setPayingId(null); setPayingObs(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const doCancel = async () => {
    try {
      await api.post(`/payment-orders/${cancelingId}/cancel`, { motivo: cancelMotivo });
      toast.success("Ordem cancelada");
      setCancelingId(null); setCancelMotivo(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (op) => {
    if (!window.confirm(`Excluir ${op.numero} definitivamente?`)) return;
    try {
      await api.delete(`/payment-orders/${op.id}`);
      toast.success("Excluída");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const printPDF = async (id) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${REACT_APP_BACKEND_URL}/api/payment-orders/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Cria um anchor invisível e clica — funciona mesmo com popup blocker
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
    } catch (err) {
      toast.error(`Erro ao gerar PDF: ${err.message || err}`);
    }
  };

  const filtered = items.filter((op) => {
    if (!q) return true;
    const hay = `${op.numero} ${op.beneficiario} ${op.descricao} ${op.cnpj_cpf}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div data-testid="payment-orders-page">
      <SectionTitle action={canManage && <Btn onClick={() => { setEditingId(null); setForm(emptyForm()); setOpen(true); }} data-testid="add-op-btn"><Plus className="w-4 h-4" />Nova Ordem</Btn>}>
        <DollarSign className="inline w-5 h-5 -mt-1 text-[#3D6B57]" /> Ordens de Pagamento
      </SectionTitle>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Total de Ordens</div><div className="font-display font-black text-2xl" data-testid="op-count">{meta.total}</div></div>
        <div className="brutal-card-sm p-3 !bg-[#FFC107] !text-[#111]"><div className="font-mono-data text-[10px] uppercase tracking-widest">Pendente</div><div className="font-display font-black text-2xl" data-testid="op-pendente">{fmtBRL(meta.total_pendente)}</div></div>
        <div className="brutal-card-sm p-3 !bg-[#1A7F37] !text-white"><div className="font-mono-data text-[10px] uppercase tracking-widest opacity-80">Já pago</div><div className="font-display font-black text-2xl" data-testid="op-pago">{fmtBRL(meta.total_pago)}</div></div>
      </div>

      {/* Filters */}
      <div className="brutal-card-sm p-3 mb-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#777]" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por Nº, beneficiário, CNPJ..." className="w-full border-2 border-[#111] pl-8 pr-2 py-2 text-sm bg-white font-mono-data" data-testid="op-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid="op-status-filter">
          <option value="">Todos status</option>
          <option value="pendente">Pendentes</option>
          <option value="paga">Pagas</option>
          <option value="cancelada">Canceladas</option>
        </select>
      </div>

      {/* Table */}
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table" data-testid="op-table">
          <thead><tr>
            <th>Nº</th>
            <th>Data</th>
            <th>Beneficiário</th>
            <th>Vencimento</th>
            <th>Forma</th>
            <th className="text-right">Valor</th>
            <th>Status</th>
            <th className="w-40">Ações</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <EmptyState message="Nenhuma ordem de pagamento." cols={8} />}
            {filtered.map((op) => (
              <tr key={op.id} data-testid={`op-row-${op.id}`}>
                <td className="font-mono-data font-bold text-sm text-[#3D6B57]">{op.numero}</td>
                <td className="font-mono-data text-xs">{fmtDate(op.created_at)}</td>
                <td>
                  <div className="font-medium">{op.beneficiario}</div>
                  {op.cnpj_cpf && <div className="font-mono-data text-[10px] text-[#777]">{op.cnpj_cpf}</div>}
                </td>
                <td className="font-mono-data text-xs">{op.data_vencimento ? op.data_vencimento.slice(0, 10).split("-").reverse().join("/") : "—"}</td>
                <td><Badge variant="outline">{(op.forma_pagamento || "").replace("_", " ")}</Badge></td>
                <td className="text-right font-mono-data font-bold">{fmtBRL(op.valor_total)}</td>
                <td>
                  {op.status === "pendente" && <Badge variant="warn">Pendente</Badge>}
                  {op.status === "paga" && <Badge variant="success">Paga</Badge>}
                  {op.status === "cancelada" && <Badge variant="danger">Cancelada</Badge>}
                </td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => openWhatsapp("", formatPaymentOrderMessage(op))}
                      data-testid={`op-whatsapp-${op.id}`}
                      className="p-1.5 border-2 border-[#111] hover:bg-[#25D366] hover:text-white"
                      title="Compartilhar via WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => printPDF(op.id)} className="p-1.5 border-2 border-[#111] hover:bg-[#3D6B57] hover:text-white" title="Imprimir PDF" data-testid={`op-print-${op.id}`}><Printer className="w-3.5 h-3.5" /></button>
                    {op.status === "pendente" && canManage && (
                      <>
                        <button onClick={() => { setPayingId(op.id); setPayingObs(""); }} className="p-1.5 border-2 border-[#1A7F37] bg-[#1A7F37] text-white hover:opacity-80" title="Marcar como paga" data-testid={`op-pay-${op.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => startEdit(op)} className="p-1.5 border-2 border-[#111] hover:bg-[#FF4F00] hover:text-white" title="Editar" data-testid={`op-edit-${op.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => { setCancelingId(op.id); setCancelMotivo(""); }} className="p-1.5 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" title="Cancelar" data-testid={`op-cancel-${op.id}`}><Ban className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                    {user?.role === "admin" && <button onClick={() => remove(op)} className="p-1.5 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" title="Excluir" data-testid={`op-delete-${op.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Nova/Editar Ordem */}
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Editar Ordem de Pagamento" : "Nova Ordem de Pagamento"} size="xl">
        <form onSubmit={submit} className="space-y-4" data-testid="op-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Beneficiário *" required value={form.beneficiario} onChange={(v) => setForm({ ...form, beneficiario: v })} testid="op-beneficiario" placeholder="Ex: Auto Peças Central Ltda" colspan />
            <Field label="CNPJ / CPF" value={form.cnpj_cpf} onChange={(v) => setForm({ ...form, cnpj_cpf: v })} testid="op-cnpj" placeholder="00.000.000/0000-00" />
            <Field label="Data de Vencimento" type="date" value={form.data_vencimento} onChange={(v) => setForm({ ...form, data_vencimento: v })} testid="op-venc" />
          </div>

          <Field label="Finalidade / Descrição" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} testid="op-descricao" placeholder="Ex: Peças de reposição da frota — Fev/2026" />

          {/* Itens */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Itens *</label>
              <button type="button" onClick={addItem} className="text-xs font-mono-data uppercase tracking-widest text-[#FF4F00] hover:underline inline-flex items-center gap-1" data-testid="op-add-item">
                <Plus className="w-3 h-3" /> Adicionar Item
              </button>
            </div>
            <div className="border-2 border-[#111] divide-y-2 divide-[#111]">
              {form.itens.map((it, i) => {
                const isServico = ["Serviço", "Hora", "Diária", "Mês"].includes(it.unidade);
                return (
                <div key={i} className="flex flex-wrap items-center gap-2 px-2 py-2 bg-white" data-testid={`op-item-${i}`}>
                  <span className="font-mono-data font-bold text-xs w-6 text-[#3D6B57]">{i + 1}.</span>
                  <input type="text" value={it.descricao} onChange={(e) => setItemField(i, "descricao", e.target.value)} placeholder={isServico ? "Ex: Manutenção de impressora HP LaserJet · Instalação de ar-condicionado · Serviço de informática" : "Descrição do item ou serviço"} className="flex-1 min-w-[180px] border-2 border-[#111] p-2 text-sm bg-white" data-testid={`op-item-desc-${i}`} />
                  <input type="number" step="0.01" min="0.01" value={it.quantidade}
                    onChange={(e) => setItemField(i, "quantidade", e.target.value)}
                    placeholder="Qtd"
                    className={`w-20 border-2 border-[#111] p-2 text-sm font-mono-data text-right ${isServico && parseFloat(it.quantidade) === 1 ? "bg-[#F5F5F5] text-[#999]" : ""}`}
                    data-testid={`op-item-qtd-${i}`}
                    title={isServico ? "Para serviços únicos, deixe em 1. Para múltiplos (ex: 4 horas), aumente." : "Quantidade"} />
                  <select value={it.unidade} onChange={(e) => setItemField(i, "unidade", e.target.value)} className="w-28 border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid={`op-item-unid-${i}`} title="Unidade / Tipo">
                    {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <input type="number" step="0.01" min="0" value={it.valor_unitario} onChange={(e) => setItemField(i, "valor_unitario", e.target.value)} placeholder={isServico ? "Valor" : "V.Unit"} className="w-28 border-2 border-[#111] p-2 text-sm font-mono-data text-right" data-testid={`op-item-valor-${i}`} title={isServico ? "Valor do serviço" : "Valor unitário R$"} />
                  <span className="w-28 text-right font-mono-data text-sm font-bold text-[#3D6B57]" data-testid={`op-item-total-${i}`} title="Total do item">
                    {fmtBRL((parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0))}
                  </span>
                  {form.itens.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="p-2 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" data-testid={`op-remove-item-${i}`}>
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );})}
            </div>
            <p className="mt-2 text-[10px] font-mono-data text-[#777] uppercase tracking-widest">
              Dica: para <strong>serviços</strong> (manutenção, informática, ar-condicionado) escolha Serviço e coloque o valor total. Para <strong>horas técnicas</strong> use Hora e coloque quantidade × valor por hora.
            </p>
            <div className="flex justify-end mt-3 items-center gap-3 border-t-2 border-[#111] pt-3">
              <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Total</span>
              <span className="font-mono-data text-2xl font-bold text-[#3D6B57]" data-testid="op-total">{fmtBRL(total)}</span>
            </div>
          </div>

          {/* Pagamento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SelectField label="Forma de Pagamento *" value={form.forma_pagamento} onChange={(v) => setForm({ ...form, forma_pagamento: v })} options={FORMAS} testid="op-forma" />
            <Field label="Dados p/ Pagamento" value={form.dados_pagamento} onChange={(v) => setForm({ ...form, dados_pagamento: v })} testid="op-dados" placeholder="Chave PIX, banco, agência, conta..." />
          </div>

          <TextArea label="Observações" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} testid="op-obs" />

          <div className="flex justify-end gap-2 border-t-2 border-[#111] pt-3">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="op-save-btn">{editingId ? "Salvar Alterações" : "Emitir Ordem"}</Btn>
          </div>
        </form>
      </Modal>

      {/* Mark Paid Modal */}
      <Modal open={!!payingId} onClose={() => setPayingId(null)} title="Marcar Ordem como PAGA" size="md">
        <div className="space-y-3" data-testid="op-mark-paid-modal">
          <p className="text-sm text-[#555]">Confirme que o pagamento foi realizado. Opcionalmente adicione uma observação (nº de comprovante, data efetiva, etc.).</p>
          <TextArea label="Observações do pagamento" value={payingObs} onChange={setPayingObs} testid="op-pay-obs" />
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={() => setPayingId(null)}>Cancelar</Btn>
            <Btn onClick={markPaid} data-testid="op-confirm-pay-btn"><CheckCircle2 className="w-4 h-4" /> Confirmar Pagamento</Btn>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal open={!!cancelingId} onClose={() => setCancelingId(null)} title="Cancelar Ordem de Pagamento" size="md">
        <div className="space-y-3" data-testid="op-cancel-modal">
          <p className="text-sm text-[#555]">Ao cancelar, a ordem não poderá mais ser paga ou editada. Informe o motivo:</p>
          <TextArea label="Motivo do cancelamento" value={cancelMotivo} onChange={setCancelMotivo} testid="op-cancel-motivo" />
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={() => setCancelingId(null)}>Voltar</Btn>
            <Btn onClick={doCancel} data-testid="op-confirm-cancel-btn"><Ban className="w-4 h-4" /> Confirmar Cancelamento</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
